import React from "react";
import { Modal, Row, Col, Badge } from "react-bootstrap";
import { InventoryItem } from "../../services/adminInventory";
import { formatDateTime } from "../../utils/dateFormat";
import { useUserCountry } from "../../hooks/useUserCountry";
// [2026-02-13] CURRENCY_PHASE2: Centralized single-price display
import { formatCurrency } from "../../utils/priceDisplay";

interface ViewItemModalProps {
  show: boolean;
  item: InventoryItem | null;
  onHide: () => void;
}

const ViewItemModal: React.FC<ViewItemModalProps> = ({ show, item, onHide }) => {
  const country = useUserCountry();

  if (!item) return null;

  // [2026-02-13] CURRENCY_PHASE2: Single price in user's currency
  const formatPrice = () => {
    return formatCurrency(item.price, item.currency, item.convertedPrice, item.displayCurrency) || "Not set";
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return "N/A";
    return formatDateTime(dateString, country);
  };

  const getSellerName = () => {
    if (item.sellerId) {
      if (typeof item.sellerId === "object" && item.sellerId.companyName) {
        return item.sellerId.companyName;
      }
      if (
        typeof item.sellerId === "object" &&
        (item.sellerId.firstName || item.sellerId.lastName)
      ) {
        return `${item.sellerId.firstName || ""} ${item.sellerId.lastName || ""}`.trim();
      }
    }
    return "Unknown";
  };

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>View Inventory Item</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Row>
          <Col md={6}>
            <h6>Basic Information</h6>
            <table className="table table-sm">
              <tbody>
                <tr>
                  <td>
                    <strong>ID:</strong>
                  </td>
                  <td>{item._id}</td>
                </tr>
                <tr>
                  <td>
                    <strong>Manufacturer:</strong>
                  </td>
                  <td>{item.manufacturer}</td>
                </tr>
                <tr>
                  <td>
                    <strong>Part Number:</strong>
                  </td>
                  <td>{item.partNumber || "N/A"}</td>
                </tr>
                <tr>
                  <td>
                    <strong>Model:</strong>
                  </td>
                  <td>{item.model || "N/A"}</td>
                </tr>
                <tr>
                  <td>
                    <strong>Title:</strong>
                  </td>
                  <td>{item.title || "N/A"}</td>
                </tr>
                <tr>
                  <td>
                    <strong>Category:</strong>
                  </td>
                  <td>{item.category || "N/A"}</td>
                </tr>
                <tr>
                  <td>
                    <strong>Condition:</strong>
                  </td>
                  <td>
                    <Badge bg="secondary">{item.condition}</Badge>
                  </td>
                </tr>
                <tr>
                  <td>
                    <strong>Quantity:</strong>
                  </td>
                  <td>
                    <Badge bg={item.quantity > 0 ? "success" : "warning"}>{item.quantity}</Badge>
                  </td>
                </tr>
              </tbody>
            </table>
          </Col>
          <Col md={6}>
            <h6>Pricing & Location</h6>
            <table className="table table-sm">
              <tbody>
                <tr>
                  <td>
                    <strong>Price:</strong>
                  </td>
                  <td>{formatPrice()}</td>
                </tr>
                <tr>
                  <td>
                    <strong>Country:</strong>
                  </td>
                  <td>{item.country || "N/A"}</td>
                </tr>
                <tr>
                  <td>
                    <strong>Status:</strong>
                  </td>
                  <td>
                    <Badge bg={item.isActive !== false ? "success" : "danger"}>
                      {item.isActive !== false ? "Active" : "Inactive"}
                    </Badge>
                  </td>
                </tr>
              </tbody>
            </table>
          </Col>
        </Row>

        {item.description && (
          <Row className="mt-3">
            <Col>
              <h6>Description</h6>
              <div className="border p-3 bg-light">{item.description}</div>
            </Col>
          </Row>
        )}

        <Row className="mt-3">
          <Col md={6}>
            <h6>Seller Information</h6>
            <table className="table table-sm">
              <tbody>
                <tr>
                  <td>
                    <strong>Seller:</strong>
                  </td>
                  <td>{getSellerName()}</td>
                </tr>
                <tr>
                  <td>
                    <strong>Seller ID:</strong>
                  </td>
                  <td>{item.sellerId && typeof item.sellerId === "object" ? item.sellerId._id : item.sellerId}</td>
                </tr>
              </tbody>
            </table>
          </Col>
          <Col md={6}>
            <h6>Timestamps</h6>
            <table className="table table-sm">
              <tbody>
                <tr>
                  <td>
                    <strong>Updated:</strong>
                  </td>
                  <td>{formatDate(item.updatedAt)}</td>
                </tr>
              </tbody>
            </table>
          </Col>
        </Row>
      </Modal.Body>
      <Modal.Footer>
        <button className="btn-cancel-figma" onClick={onHide}>
          Close
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default ViewItemModal;
